/**
 * Type-safe enum display name utilities.
 * 
 * These utilities convert uppercase API enum values to friendly display names.
 * All mappings are type-checked to ensure they stay in sync with API values.
 */

// ============================================================================
// Enum Value Definitions (must match backend exactly)
// ============================================================================

// Account types returned by the API (e.g., "google_email", "microsoft_email")
export const AccountType = {
  GOOGLE_EMAIL: 'google_email',
  MICROSOFT_EMAIL: 'microsoft_email',
} as const

export type AccountTypeValue = (typeof AccountType)[keyof typeof AccountType]

export const ActionType = {
  MARK_READ: 'MARK_READ',
  MARK_UNREAD: 'MARK_UNREAD',
  ARCHIVE: 'ARCHIVE',
  DELETE: 'DELETE',
  MOVE: 'MOVE',
  LABEL: 'LABEL',
  DRAFT_RESPONSE: 'DRAFT_RESPONSE',
  SEND_RESPONSE: 'SEND_RESPONSE',
  REPLY_EMAIL: 'REPLY_EMAIL',
  FORWARD_EMAIL: 'FORWARD_EMAIL',
  FLAG: 'FLAG',
  UNFLAG: 'UNFLAG',
} as const

import type { InstructionScope as InstructionScopeType } from '@/api'

// Re-export the API type for convenience
export type InstructionScopeValue = InstructionScopeType

// Runtime constants for InstructionScope values (matches generated API type)
export const InstructionScope = {
  USER: 'USER',
  ACCOUNT: 'ACCOUNT',
  SERVER: 'SERVER',
  TOOL: 'TOOL',
} as const satisfies Record<string, InstructionScopeType>

export const EventStatus = {
  PENDING: 'PENDING',
  ANALYZING: 'ANALYZING',
  DONE: 'DONE',
  ERROR: 'ERROR',
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
} as const

export type EventStatusValue = (typeof EventStatus)[keyof typeof EventStatus]

export const EventActionStatus = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
} as const

export type EventActionStatusValue = (typeof EventActionStatus)[keyof typeof EventActionStatus]

// ============================================================================
// Display Name Mappings
// ============================================================================

const accountTypeDisplayNames: Record<AccountTypeValue, string> = {
  google_email: 'Google Email',
  microsoft_email: 'Microsoft Email',
}

const eventStatusDisplayNames: Record<EventStatusValue, string> = {
  PENDING: 'Pending',
  ANALYZING: 'Analyzing',
  DONE: 'Done',
  ERROR: 'Error',
  QUOTA_EXCEEDED: 'Quota Exceeded',
}

const eventActionStatusDisplayNames: Record<EventActionStatusValue, string> = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In Progress',
  SUCCESS: 'Success',
  FAILED: 'Failed',
}

// ============================================================================
// Display Name Functions
// ============================================================================

/**
 * Get display name for an account type (e.g., "google_email" -> "Google").
 * Returns the raw value if not found (graceful fallback).
 */
export function getAccountTypeDisplayName(value: string): string {
  return accountTypeDisplayNames[value as AccountTypeValue] ?? value
}

/**
 * Get display name for an event status.
 */
export function getEventStatusDisplayName(value: string): string {
  return eventStatusDisplayNames[value as EventStatusValue] ?? value
}

/**
 * Get display name for an event action status.
 */
export function getEventActionStatusDisplayName(value: string): string {
  return eventActionStatusDisplayNames[value as EventActionStatusValue] ?? value
}

// ============================================================================
// List Helpers (for dropdowns/selects)
// ============================================================================

export const EVENT_STATUS_OPTIONS = Object.values(EventStatus).map((value) => ({
  value,
  label: eventStatusDisplayNames[value],
}))
